<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-md">
          <v-card-title class="secondary white--text pa-3">
            <v-icon class="mr-3" color="white">mdi-home-plus</v-icon>
            <span class="subtitle-1 white--text font-weight-light">Create New Tenancy</span>
            <v-spacer></v-spacer>
            <v-chip class="ma-2" color="white" text-color="secondary">
              Step {{ currentStep }} of 8
            </v-chip>
          </v-card-title>
          
          <v-stepper v-model="currentStep" alt-labels>
            <v-stepper-header>
              <v-stepper-step :complete="currentStep > 1" step="1">
                Property
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 2" step="2">
                Landlord
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 3" step="3">
                Area/Branch
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 4" step="4">
                Tenancy Details
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 5" step="5">
                Tenants
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 6" step="6">
                Tenancy Fee
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 7" step="7">
                Review
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="8">
                Complete
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- Step 1: Property -->
              <v-stepper-content step="1">
                <property-step
                  v-model="formData.property"
                  :loading="loading"
                  :search-function="searchProperty"
                  @next="nextStep"
                />
              </v-stepper-content>

              <!-- Step 2: Landlord -->
              <v-stepper-content step="2">
                <landlord-step
                  v-model="formData.landlord"
                  :loading="loading"
                  :search-function="searchLandlord"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 3: Area/Branch -->
              <v-stepper-content step="3">
                <area-branch-step
                  v-model="formData.area"
                  :loading="loading"
                  :branches="branches"
                  :areas="areaOptions"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 4: Tenancy Details -->
              <v-stepper-content step="4">
                <tenancy-details-step
                  v-model="formData.tenancy"
                  :loading="loading"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 5: Tenants -->
              <v-stepper-content step="5">
                <tenants-step
                  v-model="formData.tenants"
                  :loading="loading"
                  :search-function="searchTenant"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 6: Tenancy Management Fee Override -->
              <v-stepper-content step="6">
                <property-management-fee-step
                  v-model="formData.propertyManagementFee"
                  :landlord-data="formData.landlord"
                  :loading="loading"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 7: Review -->
              <v-stepper-content step="7">
                <review-step
                  :form-data="formData"
                  :loading="submitting"
                  @submit="submitForm"
                  @back="previousStep"
                />
              </v-stepper-content>

              <!-- Step 8: Complete -->
              <v-stepper-content step="8">
                <complete-step
                  :result="submissionResult"
                  @new-tenancy="resetForm"
                  @finish="$router.push('/agentpay/properties')"
                />
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import PropertyStep from '@/components/Tenancies/Steps/PropertyStep.vue'
import PropertyManagementFeeStep from '@/components/Tenancies/Steps/PropertyManagementFeeStep.vue'
import LandlordStep from '@/components/Tenancies/Steps/LandlordStep.vue'
import AreaBranchStep from '@/components/Tenancies/Steps/AreaBranchStep.vue'
import TenancyDetailsStep from '@/components/Tenancies/Steps/TenancyDetailsStep.vue'
import TenantsStep from '@/components/Tenancies/Steps/TenantsStep.vue'
import ReviewStep from '@/components/Tenancies/Steps/ReviewStep.vue'
import CompleteStep from '@/components/Tenancies/Steps/CompleteStep.vue'

export default {
  name: 'AddTenancy',
  
  components: {
    PropertyStep,
    PropertyManagementFeeStep,
    LandlordStep,
    AreaBranchStep,
    TenancyDetailsStep,
    TenantsStep,
    ReviewStep,
    CompleteStep
  },
  
  data() {
    return {
      currentStep: 1,
      submitting: false,
      submissionResult: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'info',
      formData: {
        property: {
          isExisting: null,
          existingProperty: null,
          newProperty: {
            addressNumber: '',
            address1: '',
            address2: '',
            address3: '',
            address4: '',
            postcode: '',
            type: 0,
            description: '',
            facilities: ''
          }
        },
        propertyManagementFee: {
          overrideEnabled: false,
          managementFeePercent: null,
          dateManagementFee: null
        },
        landlord: {
          isExisting: null,
          existingLandlord: null,
          newLandlord: {
            title: '',
            forename: '',
            middleName: '',
            surname: '',
            dateOfBirth: null,
            address1: '',
            address2: '',
            address3: '',
            address4: '',
            postcode: '',
            mobilePhone: '',
            contactEmail: '',
            emailLetters: true,
            managementFeePercent: null,
            dateManagementFee: null
          }
        },
        area: {
          isExisting: null,
          existingArea: null,
          newArea: {
            branchId: null,
            areaName: ''
          }
        },
        tenancy: {
          stage: null,
          advertisedRent: null,
          deposit: null,
          serviceTypeId: null,
          startDate: null,
          fixedDate: null,
          endDate: null
        },
        tenants: []
      }
    }
  },
  
  computed: {
    ...mapState('TenanciesManagement', ['loading']),
    ...mapState(['allBranches']),
    ...mapGetters('TenanciesManagement', ['areaOptions']),
    
    branches() {
      return this.allBranches.filter(b => b.OID !== null)
    }
  },
  
  async mounted() {
    try {
      await Promise.all([
        this.initializeFormData(),
        this.getAllBranches()
      ])
    } catch (error) {
      this.showSnackbar('Failed to load form data. Please refresh the page.', 'error')
    }
  },
  
  methods: {
    ...mapActions('TenanciesManagement', [
      'initializeFormData',
      'searchEntity',
      'createTenancy'
    ]),
    ...mapActions(['getAllBranches']),
    
    nextStep() {
      if (this.currentStep < 8) {
        this.currentStep++
      }
    },
    
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    
    async searchProperty(searchTerm) {
      return await this.searchEntity({
        schemaType: 'PropertyOwnable',
        searchTerm
      })
    },
    
    async searchLandlord(searchTerm) {
      return await this.searchEntity({
        schemaType: 'LettingsLandlord',
        searchTerm
      })
    },
    
    async searchTenant(searchTerm) {
      return await this.searchEntity({
        schemaType: 'LettingsTenant',
        searchTerm
      })
    },
    
    async submitForm() {
      this.submitting = true
      
      try {
        // Prepare the tenancy data for submission
        const tenancyData = this.prepareTenancyData()
        
        // Submit the form
        const result = await this.createTenancy(tenancyData)
        
        this.submissionResult = {
          success: true,
          message: 'Tenancy created successfully!',
          tenancyId: result.tenancyId || result.TenancyID || result.OID || result.data?.TenancyID
        }
        
        // Move to complete step
        this.currentStep = 8
        
      } catch (error) {
        this.showSnackbar(error.message || 'Failed to create tenancy. Please try again.', 'error')
        this.submissionResult = {
          success: false,
          message: error.message || 'Failed to create tenancy'
        }
      } finally {
        this.submitting = false
      }
    },
    
    prepareTenancyData() {
      // Format dates to ISO string format expected by API
      const formatDateToISO = (dateString) => {
        if (!dateString) return null
        const date = new Date(dateString)
        return date.toISOString()
      }
      
      const data = {
        Tenancy: {
          AreaID: this.formData.area.isExisting 
            ? this.formData.area.existingArea.value 
            : null,
          AreaName: !this.formData.area.isExisting 
            ? this.formData.area.newArea.areaName 
            : null,
          BranchID: this.formData.area.isExisting 
            ? (this.formData.area.existingArea.branchOID || null)
            : this.formData.area.newArea.branchId,
          AdvertisedRent: this.formData.tenancy.advertisedRent,
          Deposit: this.formData.tenancy.deposit,
          ServiceTypeID: this.formData.tenancy.serviceTypeId,
          Stage: this.formData.tenancy.stage,
          StartDate: formatDateToISO(this.formData.tenancy.startDate),
          FixedDate: formatDateToISO(this.formData.tenancy.fixedDate),
          EndDate: formatDateToISO(this.formData.tenancy.endDate)
        },
        
        Property: {
          PropertyID: this.formData.property.isExisting 
            ? this.formData.property.existingProperty.OID 
            : null,
          AddressNumber: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.addressNumber,
          Address1: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.address1,
          Address2: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.address2,
          Address3: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.address3,
          Address4: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.address4,
          Postcode: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.postcode,
          Type: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.type,
          Description: this.formData.property.isExisting 
            ? null 
            : this.formData.property.newProperty.description,
          Facilities: this.formData.property.isExisting
            ? null
            : this.formData.property.newProperty.facilities,
          ManagementFeePercent: this.formData.propertyManagementFee.overrideEnabled &&
                                this.formData.propertyManagementFee.managementFeePercent !== null &&
                                this.formData.propertyManagementFee.managementFeePercent !== ''
            ? this.formData.propertyManagementFee.managementFeePercent
            : null,
          DateManagementFee: this.formData.propertyManagementFee.overrideEnabled &&
                             this.formData.propertyManagementFee.dateManagementFee
            ? this.formData.propertyManagementFee.dateManagementFee
            : null
        },
        
        Landlord: {
          LandlordID: this.formData.landlord.isExisting 
            ? this.formData.landlord.existingLandlord.OID 
            : null,
          Title: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.title,
          Forename: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.forename,
          MiddleName: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.middleName,
          Surname: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.surname,
          DOB: this.formData.landlord.isExisting 
            ? null 
            : formatDateToISO(this.formData.landlord.newLandlord.dateOfBirth),
          Address1: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.address1,
          Address2: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.address2,
          Address3: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.address3,
          Address4: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.address4,
          Postcode: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.postcode,
          MobilePhone: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.mobilePhone,
          ContactEmail: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.contactEmail,
          EmailLetters: this.formData.landlord.isExisting 
            ? null 
            : this.formData.landlord.newLandlord.emailLetters
        },
        
        Tenants: this.formData.tenants.map((tenant, index) => {
          if (tenant.isExisting) {
            return {
              TenantID: tenant.existingTenant.OID,
              Title: null,
              Forename: null,
              MiddleName: null,
              Surname: null,
              DateOfBirth: null,
              EmailAddress: null,
              EmailLetters: null,
              SendTenantAppInvite: null,
              IsHeadTenant: index === 0
            }
          } else {
            return {
              TenantID: null,
              Title: tenant.newTenant.title,
              Forename: tenant.newTenant.forename,
              MiddleName: tenant.newTenant.middleName,
              Surname: tenant.newTenant.surname,
              DateOfBirth: formatDateToISO(tenant.newTenant.dateOfBirth),
              EmailAddress: tenant.newTenant.emailAddress,
              EmailLetters: tenant.newTenant.emailLetters,
              SendTenantAppInvite: tenant.newTenant.sendTenantAppInvite,
              IsHeadTenant: index === 0 || tenant.newTenant.isHeadTenant
            }
          }
        })
      }

      // Add management fee fields if both are provided when creating new landlord
      if (!this.formData.landlord.isExisting) {
        const nl = this.formData.landlord.newLandlord
        if (nl.managementFeePercent !== null &&
            nl.managementFeePercent !== '' &&
            nl.dateManagementFee) {
          data.Landlord.ManagementFeePercent = nl.managementFeePercent
          data.Landlord.DateManagementFee = nl.dateManagementFee
        }
      }

      return data
    },
    
    resetForm() {
      this.currentStep = 1
      this.submissionResult = null
      
      // Reset form data
      this.formData = {
        property: {
          isExisting: null,
          existingProperty: null,
          newProperty: {
            addressNumber: '',
            address1: '',
            address2: '',
            address3: '',
            address4: '',
            postcode: '',
            type: 0,
            description: '',
            facilities: ''
          }
        },
        propertyManagementFee: {
          overrideEnabled: false,
          managementFeePercent: null,
          dateManagementFee: null
        },
        landlord: {
          isExisting: null,
          existingLandlord: null,
          newLandlord: {
            title: '',
            forename: '',
            middleName: '',
            surname: '',
            dateOfBirth: null,
            address1: '',
            address2: '',
            address3: '',
            address4: '',
            postcode: '',
            mobilePhone: '',
            contactEmail: '',
            emailLetters: true,
            managementFeePercent: null,
            dateManagementFee: null
          }
        },
        area: {
          isExisting: null,
          existingArea: null,
          newArea: {
            branchId: null,
            areaName: ''
          }
        },
        tenancy: {
          stage: null,
          advertisedRent: null,
          deposit: null,
          serviceTypeId: null,
          startDate: null,
          fixedDate: null,
          endDate: null
        },
        tenants: []
      }
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  },
  
  beforeRouteLeave(to, from, next) {
    if (this.currentStep > 1 && this.currentStep < 7) {
      const answer = window.confirm('Do you really want to leave? You have unsaved changes!')
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  }
}
</script>

<style scoped>
.v-stepper__content {
  padding: 24px;
}
</style>